import 'cropperjs/dist/cropper.css';

import { FileResponsePrivate } from '@tests/types';
import { Col, Row } from 'antd';
import classNames from 'classnames';
import { dec, inc } from 'ramda';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';

import { CloseIcon, TurnLeft, Undo } from '@/assets';
import { Button, message } from '@/atoms';
import { ButtonType } from '@/types';

import { DrawerWrapper } from '../DrawerWrapper';
import styles from './styles.module.scss';

enum Direction {
  Left = 'Left',
  Right = 'Right',
}

interface Props {
  aspectRatio?: number;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void>;
  originalImage: FileResponsePrivate<string>;
}

const ImageCropperModal: React.FC<Props> = (props) => {
  const { aspectRatio, onClose: handleClose, onSave, originalImage } = props;
  const imageUrl = originalImage.url;

  const cropperRef = useRef<ReactCropperElement>(null);
  const [loading, setLoading] = useState(false);
  const [cropHistory, setCropHistory] = useState<string[]>([]);
  const imageComputedList = useMemo(() => [imageUrl, ...cropHistory], [imageUrl, cropHistory]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleRemoveFromHistory = () => {
    setCropHistory([]);
    setCurrentImageIndex(0);
  };

  const handleUndo = () => {
    setCurrentImageIndex(dec);
  };

  const handleNext = () => {
    setCurrentImageIndex(inc);
  };

  const handleRotate = (direction: Direction) => {
    if (cropperRef.current.cropper) {
      cropperRef.current.cropper.rotate(direction === Direction.Left ? -90 : 90);

      cropperRef.current.cropper.zoomTo(0.1);
      cropperRef.current.cropper.setCanvasData({ left: 0 });

      cropperRef.current.cropper.setAspectRatio(null);

      const croppedImage = cropperRef.current.cropper.getCroppedCanvas({
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (currentImageIndex !== imageComputedList.length - 1) {
        setCropHistory([
          ...cropHistory.slice(0, currentImageIndex),
          croppedImage.toDataURL(originalImage.contentType, 1),
        ]);
      } else {
        setCropHistory([...cropHistory, croppedImage.toDataURL(originalImage.contentType, 1)]);
      }

      setCurrentImageIndex(inc(currentImageIndex));
      cropperRef.current.cropper.setAspectRatio(aspectRatio);
    }
  };

  const handleCrop = () => {
    const croppedImage = cropperRef.current.cropper.getCroppedCanvas();

    if (currentImageIndex !== imageComputedList.length - 1) {
      setCropHistory([
        ...cropHistory.slice(0, currentImageIndex),
        croppedImage.toDataURL(originalImage.contentType, 1),
      ]);
    } else {
      setCropHistory([...cropHistory, croppedImage.toDataURL(originalImage.contentType, 1)]);
    }

    setCurrentImageIndex(inc(currentImageIndex));
  };

  const handleUploadCroppedImage = async () => {
    if (!cropperRef.current) return;

    const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    if (!croppedCanvas) {
      message({ description: 'Не удалось обработать изображение', title: 'Ошибка', type: 'error' });

      return;
    }

    croppedCanvas.toBlob(async (blob: Blob) => {
      if (!blob) {
        message({ description: 'Файл пуст', title: 'Ошибка', type: 'error' });

        return;
      }

      try {
        await onSave(blob);
        handleClose();
      } catch (error) {
        console.error('Ошибка при обработке изображения:', error);
        message({
          description: 'Произошла ошибка при обработке изображения',
          title: 'Ошибка',
          type: 'error',
        });
      }
    }, originalImage.contentType);
  };

  const handleSave = async () => {
    await handleUploadCroppedImage();
  };

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (loading) {
      cropperRef.current.cropper.setAspectRatio(aspectRatio);
    }
  }, [aspectRatio]);

  return (
    <DrawerWrapper
      desktopModal
      handleClose={handleClose}
      open
      scrollActive
      title="Кадрирование изображения"
      modalClassName={styles.modal}
      mobileDimension={768}
      mobileTitle="Кадрирование"
      handleSave={handleSave}
      footer={
        <Row justify="space-between">
          <div className={styles.modalFooterControl}>
            <Button
              disabled={imageComputedList.length < 2}
              className={styles.iconButton}
              onClick={handleRemoveFromHistory}
            >
              <CloseIcon className={styles.closeIcon} />
            </Button>
            <div className={styles.modalFooterControlHistory}>
              <Button
                disabled={imageComputedList.length < 2 || currentImageIndex < 1}
                className={styles.iconButton}
                onClick={handleUndo}
                type={ButtonType.Secondary}
                square
              >
                <Undo />
              </Button>
              <Button
                disabled={
                  imageComputedList.length < 2 || currentImageIndex === imageComputedList.length - 1
                }
                className={classNames(styles.iconButton, styles.iconNext)}
                onClick={handleNext}
                type={ButtonType.Secondary}
                square
              >
                <Undo />
              </Button>
            </div>
          </div>
          <Col md={{ flex: 'none' }}>
            <Button type={ButtonType.Primary} onClick={handleCrop} className={styles.button}>
              Применить
            </Button>
          </Col>
        </Row>
      }
    >
      <div className={styles.modalContent}>
        <div className={styles.croppedBlock}>
          <div className={styles.cropContainer}>
            <Cropper
              src={imageComputedList[currentImageIndex]}
              className={styles.croppedArea}
              guides={false}
              ref={cropperRef}
              autoCropArea={1}
              zoomOnWheel={false}
              zoomOnTouch={false}
              dragMode="none"
              viewMode={2}
              aspectRatio={aspectRatio}
            />
            <div className={styles.rotateButtons}>
              <div className={styles.rotateButton} onClick={() => handleRotate(Direction.Left)}>
                <TurnLeft />
              </div>
              <div
                className={classNames(styles.rotateButton, styles.rotateButtonNext)}
                onClick={() => handleRotate(Direction.Right)}
              >
                <TurnLeft />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DrawerWrapper>
  );
};

export default ImageCropperModal;
